/**
 * Case Studies Block
 *
 * The cards are a carousel at every width, so unlike Latest News there is no
 * breakpoint at which the instance is destroyed and a grid takes over.
 *
 * How wide a card is is not this file's business: it is a sum worked out in
 * case-studies.scss against .container-wide, so that the second card finishes
 * on the container's right edge whatever the viewport is doing. slidesPerView
 * is therefore "auto" — Swiper measures the cards rather than dividing the row
 * into a count of them, which is the only mode that lets the stylesheet own the
 * figure. A count here would have to be recalculated against the container's
 * gutter on every resize, and would still only approximate it.
 *
 * The gap is the one figure Swiper does need, because it sets the space between
 * slides itself. It too is set in the stylesheet, read off the block here and
 * read again on every resize so the two cannot drift apart.
 *
 * Which cards are dimmed is worked out in markCroppedSlides() below, from
 * Swiper's own measurements but with a pixel of tolerance Swiper does not allow
 * itself — see the note there. watchSlidesProgress is on because it is what has
 * Swiper take those measurements at all. The opacity itself is in the
 * stylesheet.
 *
 * Only the modules this block uses are imported, not swiper/bundle — webpack
 * splits everything under node_modules/swiper into
 * dist/assets/js/shared/swiper.js, and generate-acf-blocks.js makes that chunk
 * a dependency of this script (see the `blockUsesSwiper` check there), so this
 * block and the hero carousel on one page still share a single copy.
 */

import Swiper from 'swiper';
import { A11y, Navigation, Pagination } from 'swiper/modules';

const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * A number set as a custom property on the block, so the layout figures live in
 * the stylesheet alone.
 *
 * getComputedStyle() resolves an ordinary property to a used value but hands a
 * custom property back as the token it was written as — so the properties this
 * reads are written in case-studies.scss as a bare number or a bare px length.
 * A clamp() would arrive here as its own source text and a rem as the number in
 * front of the unit, and the second of those fails quietly: parseFloat("1.25rem")
 * is 1.25, which is a perfectly good number and a useless gap.
 */
function cssNumber(block, property, fallback) {
    const value = parseFloat(
        getComputedStyle(block).getPropertyValue(property),
    );

    return Number.isFinite(value) ? value : fallback;
}

/**
 * The gap the stylesheet sets, under Swiper's own option name.
 *
 * It steps at the mobile breakpoint, which is why it is read rather than
 * assumed — and read again on every resize below.
 */
function layoutFrom(block) {
    return {
        spaceBetween: cssNumber(block, '--case-studies-gap', 24),
    };
}

/**
 * How far over an edge of the row a card may measure and still count as whole.
 *
 * The sum in markCroppedSlides() weighs figures the browser rounds to whole
 * pixels — a card's offsetLeft, the carousel's clientWidth — against ones it
 * does not: the card's own width, and the distance the row has been moved
 * along. The card width here is fractional by construction, being half a
 * container less half a gap, so the two disagree by up to a pixel either way,
 * and which way depends on the width of the viewport.
 *
 * Swiper asks the same question of its own .swiper-slide-fully-visible without
 * any tolerance at all, which is why the dimming no longer keys off that class.
 * At every viewport width where a card's true left edge lands on a half pixel,
 * offsetLeft rounds up past it and the card is judged to be hanging over an
 * edge it is in fact flush against — so the card you have just navigated to is
 * dimmed while you are looking at it. Most visible on the last card, where the
 * run has stopped and there is plainly nothing past it to be cut off by.
 *
 * A pixel is the tolerance Swiper itself allows when deciding it has reached
 * either end of the run, and a card cropped by less than one is not cropped.
 */
const SLIDE_EDGE_TOLERANCE = 1;

/**
 * Marks each card the row cannot fit whole, which is what the dimming in
 * case-studies.scss keys off.
 *
 * Swiper's own measurements, read back rather than taken again: the offset is
 * where the card sits in the row, the translate how far the row has been moved
 * along, and the size the width of the window the two are seen through. They
 * are current whenever this runs — watchSlidesProgress has Swiper re-measure
 * the offsets on every pass over the slides, and the setTranslate event is
 * emitted after the new translate has been stored.
 */
function markCroppedSlides(swiper) {
    swiper.slides.forEach((slide, index) => {
        const start = slide.swiperSlideOffset + swiper.translate;
        const end = start + swiper.slidesSizesGrid[index];

        slide.classList.toggle(
            'case-studies__item--cropped',
            start < -SLIDE_EDGE_TOLERANCE ||
                end > swiper.size + SLIDE_EDGE_TOLERANCE,
        );
    });
}

function initCaseStudies(scope = document) {
    scope.querySelectorAll('.case-studies').forEach((block) => {
        if (block.dataset.initialised) return;
        block.dataset.initialised = 'true';

        const carousel = block.querySelector('.case-studies__carousel');

        if (!carousel) return;

        // One card is already the finished row — there is nothing to move
        // through, and the template renders no controls for it either.
        if (carousel.querySelectorAll('.swiper-slide').length < 2) return;

        // Before Swiper, not after: the fallback row carries its own CSS gap
        // between the cards, and this class is what drops it so Swiper can set
        // the spacing itself. Added afterwards, Swiper measures a row still
        // holding both — every card reads 24px further along than it is, and
        // the second one is judged to be off the edge and dimmed. It corrects
        // itself on the first resize, which is why it shows up as a card that
        // is dim on load and fine forever after.
        block.classList.add('case-studies--carousel');

        new Swiper(carousel, {
            modules: [A11y, Navigation, Pagination],
            // The cards size themselves — see case-studies.scss.
            slidesPerView: 'auto',
            ...layoutFrom(block),
            speed: prefersReducedMotion() ? 0 : 500,
            // Not for Swiper's own visible/fully-visible classes, which the
            // dimming no longer keys off, but for what is measured to set them:
            // this is the only thing that has Swiper record where each card
            // sits in the row, and re-record it on resize, and that offset is
            // what markCroppedSlides() reads.
            watchSlidesProgress: true,
            // The arrows are disabled at either end rather than looping, so the
            // run has a start and a finish to feel out. This also locks the
            // whole carousel — dots and arrows included — at any width where
            // every card already fits.
            watchOverflow: true,
            navigation: {
                prevEl: block.querySelector('.case-studies__arrow--prev'),
                nextEl: block.querySelector('.case-studies__arrow--next'),
            },
            pagination: {
                el: block.querySelector('.case-studies__pagination'),
                clickable: true,
            },
            a11y: {
                enabled: true,
                containerRoleDescriptionMessage: 'carousel',
                itemRoleDescriptionMessage: 'slide',
                slideLabelMessage: 'Case study {{index}} of {{slidesLength}}',
                paginationBulletMessage: 'Go to case study {{index}}',
            },
            on: {
                // The dimming, marked at load and kept up to date from there.
                // setTranslate is emitted on every change of the row's position
                // — an arrow, a dot, a swipe, a resize — and after the new
                // translate has been stored, which is the figure being read;
                // the progress event is emitted before it on some paths and
                // would leave the classes a move behind.
                init: markCroppedSlides,
                setTranslate: markCroppedSlides,

                // beforeResize, not resize: Swiper emits this one first and
                // re-measures on the second, so putting the new gap on params
                // here means its own pass picks it up. Handling `resize`
                // instead would mean measuring once against the old figure and
                // then calling update() to do it all again.
                beforeResize: (swiper) => {
                    Object.assign(swiper.params, layoutFrom(block));
                },
            },
        });
    });
}

document.addEventListener('DOMContentLoaded', () => initCaseStudies());

// Blocks are re-rendered as they are edited, so run again on each preview
// render (dispatched by resources/js/editor-block-init.js).
document.addEventListener('blockEditorInit', (e) => {
    if (e.detail && e.detail.block) {
        initCaseStudies(e.detail.block);
    }
});
